import React, { useMemo, useState } from 'react';
import { useData } from '../lib/DataContext.jsx';
import { SectionTitle, ProvenanceFooter, BandPill, ScoreCell, Chip, ProvenanceBadge } from '../components/ui/kits.jsx';
import IndiaChoropleth from '../components/charts/IndiaChoropleth.jsx';
import { fmt, fmtCr, fmtInt, bandColor } from '../lib/format.js';
import { ProjectCode } from '../components/ui/ProjectCode.jsx';
import { ChevronRight, ChevronDown, X, MapPin } from 'lucide-react';

export default function Geography() {
  const { data, geo, agg: A, subdistricts } = useData();
  const [selectedState, setSelectedState] = useState(null);

  // Build stat map by state (canonical name -> stat).
  // The new LGD-keyed base map matches our canonical names directly.
  const stats = useMemo(() => {
    if (!A) return {};
    const m = {};
    for (const s of A.states) m[s.state] = s;
    return m;
  }, [A]);

  if (!data || !A) return null;

  return (
    <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }} data-testid="page-geo">
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 640px) 1fr', gap: 14 }}>
        <div className="card" style={{ padding: 18 }}>
          <SectionTitle right={<ProvenanceBadge kind="live" note="LGD-keyed base map · state avg from OECD scores" />}>India · State Performance Map</SectionTitle>
          <IndiaChoropleth geojson={geo} stateStatsByName={stats} />
        </div>

        <div className="card" style={{ padding: 18 }}>
          <SectionTitle right={<span style={{ fontSize: 11, color: 'var(--ink-4)' }}>Click any state for district drill-down</span>}>
            Top States
          </SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 640, overflowY: 'auto' }}>
            {A.states.slice(0, 20).map((s, i) => (
              <button
                key={s.state}
                onClick={() => setSelectedState(s.state)}
                data-testid={`state-row-${s.state}`}
                style={{
                  display: 'grid', gridTemplateColumns: '32px 1fr 80px 90px 20px',
                  alignItems: 'center', gap: 10, padding: '8px 10px',
                  border: `1px solid ${selectedState === s.state ? 'var(--primary)' : 'var(--line)'}`,
                  borderRadius: 10,
                  background: selectedState === s.state ? 'var(--primary-soft)' : 'var(--surface-2)',
                  textAlign: 'left', cursor: 'pointer', width: '100%',
                  transition: 'all .15s',
                }}
              >
                <div className="tabular" style={{ fontSize: 20, fontWeight: 800, color: 'var(--ink-4)' }}>{i + 1}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{s.state}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{s.partner || '—'} · {s.region}</div>
                </div>
                <div className="tabular" style={{ textAlign: 'right', fontWeight: 800, color: bandColor(s.band), fontSize: 15 }}>
                  {fmt(s.avgScore, 2)}
                </div>
                <BandPill band={s.band} />
                <ChevronRight size={14} color="var(--ink-4)" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {selectedState && subdistricts && subdistricts.states[selectedState] && (
        <DrillDownPanel
          stateName={selectedState}
          subdistricts={subdistricts.states[selectedState]}
          stateStat={stats[selectedState]}
          onClose={() => setSelectedState(null)}
        />
      )}
      {selectedState && (!subdistricts || !subdistricts.states[selectedState]) && (
        <div className="card" style={{ padding: 16, borderColor: 'var(--accent)', color: 'var(--accent-2)' }} data-testid="drill-nodata">
          No sub-district data available for {selectedState}.
          <button onClick={() => setSelectedState(null)} style={{ marginLeft: 12, background: 'transparent', border: '1px solid currentColor', padding: '4px 10px', borderRadius: 6, color: 'inherit', cursor: 'pointer' }}>Close</button>
        </div>
      )}

      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ProvenanceBadge kind="live" />
            <span style={{ fontSize: 11, color: 'var(--ink-4)' }} className="tabular">{A.states.length} states/UTs</span>
          </div>
        }>
          State × Tool Matrix
        </SectionTitle>
        <div className="scroll-y" style={{ maxHeight: 520 }}>
          <table className="pv-table">
            <thead>
              <tr>
                <th>State</th><th>Partner</th><th>Region</th>
                <th style={{ textAlign: 'right' }}>Projects</th>
                <th style={{ textAlign: 'center' }}>NRM</th>
                <th style={{ textAlign: 'center' }}>H&H</th>
                <th style={{ textAlign: 'center' }}>SDLE</th>
                <th style={{ textAlign: 'center' }}>PoE</th>
                <th style={{ textAlign: 'center' }}>Overall</th>
                <th>Band</th>
              </tr>
            </thead>
            <tbody>
              {A.states.map((s) => (
                <tr key={s.state}>
                  <td style={{ fontWeight: 700 }}>{s.state}</td>
                  <td style={{ fontSize: 11.5, color: 'var(--ink-2)' }}>
                    {s.partner || '—'}{s.partnersMore > 0 && <span style={{ color: 'var(--ink-4)' }}> +{s.partnersMore}</span>}
                  </td>
                  <td style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{s.region} · {s.zone}</td>
                  <td className="tabular" style={{ textAlign: 'right' }}>{s.projects}</td>
                  <td style={{ textAlign: 'center' }}><ScoreCell score={s.tools.NRM} /></td>
                  <td style={{ textAlign: 'center' }}><ScoreCell score={s.tools['H&H']} /></td>
                  <td style={{ textAlign: 'center' }}><ScoreCell score={s.tools.SDLE} /></td>
                  <td style={{ textAlign: 'center' }}><ScoreCell score={s.tools.PoE} /></td>
                  <td style={{ textAlign: 'center' }}><ScoreCell score={s.avgScore} /></td>
                  <td><BandPill band={s.band} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ProvenanceFooter text={`Sub-district data covers ${subdistricts?.coverage?.districts || '—'} districts across ${subdistricts?.coverage?.states || '—'} states, sourced from Project_Geography_Details.csv (village-region level). Base choropleth uses the LGD-keyed states TopoJSON (Survey of India + geoBoundaries, ODbL-1.0) — post-reorg with Telangana & Ladakh as separate features.`} />
    </div>
  );
}

function DrillDownPanel({ stateName, subdistricts, stateStat, onClose }) {
  const [openDistrict, setOpenDistrict] = useState(null);
  const dList = subdistricts.districts;

  return (
    <div className="card fade-up" style={{ padding: 18 }} data-testid="drill-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <MapPin size={18} color="var(--primary)" />
          <div>
            <div className="crumb">Drill-down</div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>{stateName}</div>
          </div>
          {stateStat && (
            <>
              <div className="tabular" style={{ fontSize: 13, fontWeight: 700, color: bandColor(stateStat.band), marginLeft: 10 }}>
                {fmt(stateStat.avgScore, 2)}
              </div>
              <BandPill band={stateStat.band} />
              <Chip>{stateStat.projects} projects</Chip>
              <Chip>{fmtCr(stateStat.budgetCr)}</Chip>
              <Chip>{dList.length} districts</Chip>
            </>
          )}
        </div>
        <button
          onClick={onClose}
          data-testid="drill-close"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: 'transparent', border: '1px solid var(--line)',
            padding: '5px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', color: 'var(--ink-3)',
          }}
        >
          <X size={12} /> Close
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {dList.map((d) => {
          const open = openDistrict === d.name;
          return (
            <div key={d.name} data-testid={`district-${d.name}`}>
              <button
                onClick={() => setOpenDistrict(open ? null : d.name)}
                style={{
                  width: '100%', display: 'grid',
                  gridTemplateColumns: '18px 1fr auto auto auto',
                  gap: 12, alignItems: 'center',
                  padding: '10px 12px',
                  background: open ? 'var(--primary-soft)' : 'var(--surface-2)',
                  border: `1px solid ${open ? 'var(--primary)' : 'var(--line)'}`,
                  borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                  transition: 'all .15s',
                }}
              >
                {open ? <ChevronDown size={14} color="var(--primary)" /> : <ChevronRight size={14} color="var(--ink-4)" />}
                <div style={{ fontWeight: 700, fontSize: 13 }}>{d.name}</div>
                <Chip>{d.projects.length} project{d.projects.length === 1 ? '' : 's'}</Chip>
                {d.blocksCount > 0 && <Chip>{d.blocksCount} block{d.blocksCount === 1 ? '' : 's'}</Chip>}
                <Chip>{d.villagesCount} village{d.villagesCount === 1 ? '' : 's'}</Chip>
              </button>

              {open && (
                <div style={{ padding: '10px 32px 14px', background: '#fbfcfe', borderLeft: '2px solid var(--primary)', marginLeft: 12 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '.08em', marginRight: 6 }}>Projects:</span>
                    {d.projects.map((pc) => (
                      <span key={pc} style={{
                        fontSize: 11, background: 'var(--primary-soft)',
                        padding: '2px 8px', borderRadius: 4,
                      }}>
                        <ProjectCode code={pc} style={{ textDecoration: 'none', fontSize: 11 }} />
                      </span>
                    ))}
                  </div>

                  {d.blocks && d.blocks.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                      {d.blocks.map((b) => (
                        <div key={b.name} style={{
                          padding: 10, border: '1px solid var(--line)',
                          borderRadius: 8, background: 'var(--surface)',
                        }} data-testid={`block-${b.name}`}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                            <div style={{ fontSize: 12, fontWeight: 700 }}>{b.name}</div>
                            <div className="tabular" style={{ fontSize: 10.5, color: 'var(--ink-3)', fontWeight: 700 }}>
                              {b.villagesCount} village{b.villagesCount === 1 ? '' : 's'}
                            </div>
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--ink-4)', marginBottom: 4 }}>
                            {b.projects.map((p) => (
                              <span key={p} style={{ marginRight: 6 }} className="tabular">{p}</span>
                            ))}
                          </div>
                          {b.villages.length > 0 && (
                            <div style={{ fontSize: 10.5, color: 'var(--ink-3)', lineHeight: 1.4 }}>
                              {b.villages.slice(0, 6).join(', ')}
                              {b.villagesCount > 6 && <span style={{ color: 'var(--ink-4)' }}> +{b.villagesCount - 6} more</span>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 11.5, color: 'var(--ink-4)', fontStyle: 'italic' }}>
                      Block-level data not captured in the current export — village-region only.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
